import { isEmpty, forEach, isObjectLike } from 'lodash';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import { MODEL_TERM } from 'commons-vehicle/PurchaseMethods/constants/dataConversions.js';
import { WEEKLY } from 'commons-vehicle/PurchaseMethods/constants/paymentFrequencies.js';
import TermModel from 'commons-vehicle/PurchaseMethods/models/TermModel.js';
import * as PaymentFrequencyService from 'commons-vehicle/PurchaseMethods/services/PaymentFrequencyService/PaymentFrequencyService.js';
import { getRebatesDetailsFromData } from 'commons-vehicle/PurchaseMethods/services/CashPurchaseService/CashPurchaseService.js';

function resetTermFromCollectionData(collectionTermData, termIndex) {
    const {
        term,
        apr,
        frequenciesPayments,
        cashDown,
        discount,
        rebate,
        rebateTaxes,
        rebateAtDelivery,
        rebateAtStandardRate,
        rebates,
        financedAmount,
        financedAmountWithTaxes,
        residualAmount,
    } = collectionTermData;

    const paymentFrequencies = {};
    let defaultFrequency;
    forEach(frequenciesPayments, (frequency) => {
        const paymentFrequency = PaymentFrequencyService.resetPaymentFrequencyFromCollectionData(frequency, cashDown);
        paymentFrequencies[paymentFrequency.paymentFrequency] = paymentFrequency;
        defaultFrequency = paymentFrequency.paymentFrequency;
    });

    if (paymentFrequencies[WEEKLY]) {
        defaultFrequency = WEEKLY;
    }

    const rebatesDetailsData = isEmpty(rebates) ? [] : rebates;
    const rebatesDetails = getRebatesDetailsFromData(rebatesDetailsData);

    const financedAmountAdjustment = cashDown ? (Math.round(cashDown * 100) / 100) * -1 : 0;

    return new TermModel({
        term,
        termIndex,
        apr,
        residualRate: 0,
        cashDown,
        discounts: discount || 0,
        rebates: rebate,
        rebatesTaxes: rebateTaxes || 0,
        rebatesAtDelivery: rebateAtDelivery,
        rebatesAtStandardRate: rebateAtStandardRate || 0,
        paymentFrequencies,
        paymentFrequency: defaultFrequency,
        payment: paymentFrequencies[defaultFrequency].payment,
        paymentTaxes: paymentFrequencies[defaultFrequency].paymentTaxes,
        financedAmount,
        financedAmountWithTaxes,
        financedAmountAdjustment,
        financedAmountWithTaxesAdjustment: financedAmountAdjustment,
        residualAmount,
        cashDownPaymentAdjustment: paymentFrequencies[defaultFrequency].cashDownPaymentAdjustment,
        netTradeInPaymentAdjustment: paymentFrequencies[defaultFrequency].netTradeInPaymentAdjustment,
        netTradeInPaymentTaxesAdjustment: paymentFrequencies[defaultFrequency].netTradeInPaymentTaxesAdjustment,
        calculatedNetPayment: paymentFrequencies[defaultFrequency].calculatedNetPayment,
        calculatedPayment: paymentFrequencies[defaultFrequency].calculatedPayment,
        rebatesDetails,
    });
}

function resetTermFromCalculatorData(calculatorTermData, termIndex) {
    const { term, apr, frequencies, financedAmount, financedAmountWithTaxes, residualAmount } = calculatorTermData;

    const paymentFrequencies = {};
    let cashDown = 0;
    forEach(frequencies, (frequency) => {
        if (cashDown < frequency.cashDown) {
            cashDown = frequency.cashDown;
        }
    });

    let defaultFrequency;
    forEach(frequencies, (frequency) => {
        const paymentFrequency = PaymentFrequencyService.resetPaymentFrequencyFromCalculatorData(frequency, cashDown);
        paymentFrequencies[paymentFrequency.paymentFrequency] = paymentFrequency;
        defaultFrequency = paymentFrequency.paymentFrequency;
    });

    if (paymentFrequencies[WEEKLY]) {
        defaultFrequency = WEEKLY;
    }

    const financedAmountAdjustment = cashDown ? (Math.round(cashDown * 100) / 100) * -1 : 0;

    return new TermModel({
        term,
        termIndex,
        apr,
        residualRate: 0,
        cashDown,
        discounts: 0,
        rebates: 0,
        rebatesTaxes: 0,
        rebatesAtDelivery: 0,
        rebatesTaxesAtDelivery: 0,
        rebatesAtStandardRate: 0,
        paymentFrequencies,
        paymentFrequency: defaultFrequency,
        payment: paymentFrequencies[defaultFrequency].payment,
        paymentTaxes: paymentFrequencies[defaultFrequency].paymentTaxes,
        financedAmount,
        financedAmountWithTaxes,
        financedAmountAdjustment,
        financedAmountWithTaxesAdjustment: financedAmountAdjustment,
        residualAmount,
        cashDownPaymentAdjustment: paymentFrequencies[defaultFrequency].cashDownPaymentAdjustment,
        netTradeInPaymentAdjustment: paymentFrequencies[defaultFrequency].netTradeInPaymentAdjustment,
        netTradeInPaymentTaxesAdjustment: paymentFrequencies[defaultFrequency].netTradeInPaymentTaxesAdjustment,
        calculatedNetPayment: paymentFrequencies[defaultFrequency].calculatedNetPayment,
        calculatedPayment: paymentFrequencies[defaultFrequency].calculatedPayment,
    });
}

function resetTermFromModelData(modelTermData, termIndex) {
    if (modelTermData.length <= MODEL_TERM.PAYMENT_FREQUENCIES) {
        return new TermModel();
    }

    const paymentFrequencies = {};
    let defaultFrequency;
    forEach(modelTermData[MODEL_TERM.PAYMENT_FREQUENCIES], (frequency) => {
        const paymentFrequency = PaymentFrequencyService.resetPaymentFrequencyFromModelData(
            frequency,
            modelTermData[MODEL_TERM.CASH_DOWN]
        );
        paymentFrequencies[paymentFrequency.paymentFrequency] = paymentFrequency;
        defaultFrequency = paymentFrequency.paymentFrequency;
    });

    if (paymentFrequencies[WEEKLY]) {
        defaultFrequency = WEEKLY;
    }

    const rebatesDetailsData = isEmpty(modelTermData[MODEL_TERM.REBATES_DETAILS])
        ? []
        : modelTermData[MODEL_TERM.REBATES_DETAILS];
    const rebatesDetails = getRebatesDetailsFromData(rebatesDetailsData);

    const includedItemsCodes = modelTermData[MODEL_TERM.INCLUDED_CODES] || [];

    const cashDown = modelTermData[MODEL_TERM.CASH_DOWN];
    const financedAmountAdjustment = cashDown ? (Math.round(cashDown * 100) / 100) * -1 : 0;

    return new TermModel({
        term: modelTermData[MODEL_TERM.TERM],
        termIndex,
        apr: modelTermData[MODEL_TERM.APR],
        residualRate: modelTermData[MODEL_TERM.RESIDUAL_RATE],
        cashDown,
        discounts: modelTermData[MODEL_TERM.DISCOUNT],
        rebates: modelTermData[MODEL_TERM.REBATE],
        rebatesTaxes: modelTermData[MODEL_TERM.REBATE_TAXES] || 0,
        rebatesAtDelivery: modelTermData[MODEL_TERM.REBATE_AT_DELIVERY],
        rebatesTaxesAtDelivery: modelTermData[MODEL_TERM.REBATE_TAXES_AT_DELIVERY] || 0,
        rebatesAtStandardRate: modelTermData[MODEL_TERM.REBATE_AT_STANDARD_RATE] || 0,
        paymentFrequencies,
        paymentFrequency: defaultFrequency,
        payment: paymentFrequencies[defaultFrequency].payment,
        paymentTaxes: paymentFrequencies[defaultFrequency].paymentTaxes,
        financedAmount: modelTermData[MODEL_TERM.FINANCED_AMOUNT],
        financedAmountWithTaxes: modelTermData[MODEL_TERM.FINANCED_AMOUNT_WITH_TAXES],
        financedAmountAdjustment,
        financedAmountWithTaxesAdjustment: financedAmountAdjustment,
        residualAmount: modelTermData[MODEL_TERM.RESIDUAL_AMOUNT],
        cashDownPaymentAdjustment: paymentFrequencies[defaultFrequency].cashDownPaymentAdjustment,
        netTradeInPaymentAdjustment: paymentFrequencies[defaultFrequency].netTradeInPaymentAdjustment,
        netTradeInPaymentTaxesAdjustment: paymentFrequencies[defaultFrequency].netTradeInPaymentTaxesAdjustment,
        calculatedNetPayment: paymentFrequencies[defaultFrequency].calculatedNetPayment,
        calculatedPayment: paymentFrequencies[defaultFrequency].calculatedPayment,
        includedItemsCodes,
        rebatesDetails,
    });
}

export function resetTermFromOriginData(dataOrigin, originTermData, termIndex) {
    if (isEmpty(originTermData) || !isObjectLike(originTermData)) {
        return new TermModel();
    }

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            return resetTermFromCollectionData(originTermData, termIndex);

        case DataOrigins.PAYMENT_CALCULATOR:
            return resetTermFromCalculatorData(originTermData, termIndex);

        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            return resetTermFromModelData(originTermData, termIndex);

        default:
            return new TermModel();
    }
}

export function recalculateTermPayment(term, paymentCalculationParameters) {
    if (term.get('term') === 0 || !isObjectLike(paymentCalculationParameters)) {
        return term;
    }

    const {
        isLease,
        paymentFrequency,
        taxesIncluded,
        taxesFactor,
        cashDown,
        tradeIn,
        lien,
        exteriorColourRetail,
        exteriorColourResidualRetail,
        interiorColourRetail,
        interiorColourResidualRetail,
        optionsTotalRetail,
        optionsTotalResidualRetail,
    } = paymentCalculationParameters;

    const paymentFrequencies = term.get('paymentFrequencies');
    const currentPaymentFrequency = paymentFrequencies[paymentFrequency];
    if (!currentPaymentFrequency) {
        return term;
    }
    const payment = currentPaymentFrequency.payment;
    const paymentTaxes = currentPaymentFrequency.paymentTaxes;

    // This variable is used as a multiplier for taxes calculations
    const taxesIncludedFactor = taxesIncluded ? 1 : 0;

    const newTerm = term.set('paymentFrequency', currentPaymentFrequency.paymentFrequency);

    const optionsResidualRetail =
        exteriorColourResidualRetail + interiorColourResidualRetail + optionsTotalResidualRetail;
    const optionsPaymentAdjustment = newTerm.calculatePaymentAdjustment(
        exteriorColourRetail + interiorColourRetail + optionsTotalRetail,
        optionsResidualRetail
    );
    const optionsPaymentTaxesAdjustment = optionsPaymentAdjustment * taxesFactor;

    let netTradeInPaymentAdjustment = newTerm.calculatePaymentAdjustment(tradeIn - lien, 0);
    if (netTradeInPaymentAdjustment > payment + optionsPaymentAdjustment) {
        netTradeInPaymentAdjustment = payment + optionsPaymentAdjustment;
    }

    let netTradeInPaymentTaxesAdjustment = newTerm.calculatePaymentAdjustment(tradeIn * taxesFactor, 0);
    if (isLease) {
        netTradeInPaymentTaxesAdjustment = newTerm.calculatePaymentAdjustment((tradeIn + cashDown) * taxesFactor, 0);
    }
    if (netTradeInPaymentTaxesAdjustment > paymentTaxes + optionsPaymentTaxesAdjustment) {
        netTradeInPaymentTaxesAdjustment = paymentTaxes + optionsPaymentTaxesAdjustment;
    }

    const calculatedNetPayment =
        payment +
        optionsPaymentAdjustment -
        netTradeInPaymentAdjustment +
        (paymentTaxes + optionsPaymentTaxesAdjustment - netTradeInPaymentTaxesAdjustment) * taxesIncludedFactor;

    let cashDownPaymentAdjustment = newTerm.calculatePaymentAdjustment(cashDown, 0);
    if (cashDownPaymentAdjustment > calculatedNetPayment) {
        cashDownPaymentAdjustment = calculatedNetPayment;
    }

    const financedAmountAdjustment =
        exteriorColourRetail + interiorColourRetail + optionsTotalRetail - (tradeIn - lien) - cashDown;
    const financedAmountWithTxAdjustment =
        financedAmountAdjustment +
        (exteriorColourRetail + interiorColourRetail + optionsTotalRetail - tradeIn) * taxesFactor;

    const calculatedPayment = calculatedNetPayment - cashDownPaymentAdjustment;

    return newTerm
        .set('cashDown', cashDown)
        .set('payment', payment)
        .set('paymentTaxes', paymentTaxes)
        .set('optionsResidualRetail', optionsResidualRetail)
        .set('optionsPaymentAdjustment', optionsPaymentAdjustment)
        .set('optionsPaymentTaxesAdjustment', optionsPaymentTaxesAdjustment)
        .set('netTradeInPaymentAdjustment', netTradeInPaymentAdjustment)
        .set('netTradeInPaymentTaxesAdjustment', netTradeInPaymentTaxesAdjustment)
        .set('calculatedNetPayment', calculatedNetPayment)
        .set('cashDownPaymentAdjustment', cashDownPaymentAdjustment)
        .set('calculatedPayment', calculatedPayment)
        .set('financedAmountAdjustment', financedAmountAdjustment)
        .set('financedAmountWithTaxesAdjustment', financedAmountWithTxAdjustment);
}

export default {
    resetTermFromCollectionData,
    resetTermFromCalculatorData,
    resetTermFromModelData,
    resetTermFromOriginData,
    recalculateTermPayment,
};
